// components/ErrorBoundary/index.tsx — ErrorBoundary composition (class component)
"use client";

import React, { Component, ErrorInfo, ReactNode } from "react";
import type { ErrorInfo as ErrorHandlerErrorInfo } from "@/utilities/errorHandler";
import { createErrorInfo } from "@/utilities/errorHandler";
import { logStructuredError } from "../../utils/errorHandling";
import DefaultErrorUI from "./DefaultErrorUI";

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Custom fallback component */
  fallback?: React.ReactNode;
  /** Callback when error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Whether to show retry button */
  showRetryButton?: boolean;
  /** Retry handler callback */
  onRetry?: () => void;
  /** Reset key for remounting the boundary */
  resetKey?: number;
  /** Context label for error logging (which page/routes errored) */
  context?: string;
}

/**
 * ErrorBoundary Component
 *
 * Catches JavaScript errors in its child component tree and renders a fallback UI.
 * Provides options for customizing the error display and handling.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, { hasError: boolean; error: Error | null }> {
  public state: { hasError: boolean; error: Error | null } = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): { hasError: boolean; error: Error | null } {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorInfoData = createErrorInfo(error, {
      context: { componentStack: errorInfo.componentStack },
    });

    // Log error or send to error tracking service
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    logStructuredError(error.message, error, { page: 'ErrorBoundary', action: errorInfo.componentStack ?? undefined });

    // Persist ke error_logs via RPC (2026-09-11): layar merah sebelumnya HANYA
    // console.error → tidak ada jejak di DB → akar masalah tak terlacak.
    // logError tidak pernah throw (try/catch + rate-limit di dalam), aman di sini.
    // Dynamic import agar boundary tetap ringan & bebas circular-import.
    void import('../../utils/errorLogger/log.js').then(({ logError }) =>
      logError(error, `ErrorBoundary:${this.props.context ?? 'app'}`, {
        componentStack: errorInfo.componentStack ?? null,
        pageUrl: typeof window !== 'undefined' ? window.location.href : null,
      })
    );
  }

  handleReset = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <DefaultErrorUI
          error={this.state.error}
          showRetryButton={this.props.showRetryButton !== false}
          onReset={this.handleReset}
          onGoHome={this.handleGoHome}
        />
      );
    }

    return this.props.children;
  }
}

export { ErrorBoundary };
export default ErrorBoundary;
